import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { beep } from "@/components/inbox/inboxUtils";

const Ctx = createContext({ items: [], online: [], me: "away", aiEnabled: false, reload: () => {}, setPresence: () => {} });
export const useChat = () => useContext(Ctx);

export const LATE_MIN = 5;
export const FUNNELS = {
  futmobplus: { label: "Futmob Plus", cls: "bg-emerald-400/15 text-emerald-300" },
  futzap: { label: "Futzap", cls: "bg-cyan-400/15 text-cyan-300" },
  pipoca: { label: "Universo Pipoca", cls: "bg-pink-400/15 text-pink-300" },
  suporte: { label: "Atendimento de Suporte", cls: "bg-orange-400/20 text-orange-300" },
};
export const funnelOf = (s) => FUNNELS[s.funil] ? s.funil : "futmobplus";
export const waitMin = (s) => (s.waiting_since ? Math.floor((Date.now() - new Date(s.waiting_since)) / 60000) : 0);
export const isChatLate = (s) => s.needs_human || (s.waiting_since && waitMin(s) >= LATE_MIN);
const keyOf = (s) => `${s.id}|${s.last_at}`;

export const ChatProvider = ({ children }) => {
  const [d, setD] = useState({ items: [], online: [], me: "away", ai_enabled: false });
  const seen = useRef(null);
  const nav = useNavigate();

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/sessions", { params: { status: "open" } });
      const fresh = data.items.filter((s) => s.last_from === "visitor" && seen.current && !seen.current.has(keyOf(s)));
      if (fresh.length) {
        beep();
        const s = fresh[0];
        toast(`Chat do site: ${s.name}`, { description: s.last_text, action: { label: "Abrir", onClick: () => nav(`/chat?s=${s.id}`) } });
        if (document.hidden && "Notification" in window && Notification.permission === "granted") {
          new Notification(`Chat do site: ${s.name}`, { body: s.last_text });
        }
      }
      seen.current = new Set(data.items.map(keyOf));
      setD(data);
    } catch { /* keep last state */ }
  }, [nav]);

  useEffect(() => {
    const h = Number(new Date().toLocaleString("en-US", { timeZone: "America/Sao_Paulo", hour: "numeric", hourCycle: "h23" }));
    if (!sessionStorage.getItem("chat_auto_presence") && h >= 9 && h < 22) {
      sessionStorage.setItem("chat_auto_presence", "1");
      api.post("/chat/presence", { status: "available" }).then(reload).catch(() => {});
    }
  }, [reload]);

  useEffect(() => {
    reload();
    const t = setInterval(reload, 4000);
    return () => clearInterval(t);
  }, [reload]);

  const setPresence = async (status) => {
    await api.post("/chat/presence", { status });
    toast.success(status === "available" ? "Você está disponível no chat" : "Você está ausente no chat");
    reload();
  };

  return (
    <Ctx.Provider value={{ items: d.items, online: d.online, me: d.me, aiEnabled: d.ai_enabled, reload, setPresence }}>
      {children}
    </Ctx.Provider>
  );
};
